import { PromoCampaignService } from '@analytics/feature';
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActiveCartService, CartActions, MultiCartService } from '@cart/data-access';
import { CheckoutActions, CheckoutApiService } from '@checkout-v2/data-access';
import { Actions, EffectNotification, OnRunEffects, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { RoutingService, UserIdService, WindowRef, normalizeHttpError } from '@spartacus/core';
import { CurrentProfileService } from '@user/feature';
import { Observable, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { CheckoutContactFormService, CheckoutService } from '../services';

@Injectable()
export class CheckoutEffects implements OnRunEffects {
  // редирект из чекаута если корзина пуста или с ошибкой
  redirectFromCheckoutIfCartIsEmpty$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CartActions.LoadCartSuccess | CartActions.LoadCartFail>(
          CartActions.LOAD_CART_SUCCESS,
          CartActions.LOAD_CART_FAIL,
        ),
        filter(
          (action) =>
            (action.type === CartActions.LOAD_CART_SUCCESS && action.payload.cart.entries?.length === 0) ||
            action.type === CartActions.LOAD_CART_FAIL,
        ),
        tap(() => this.routingService.go({ cxRoute: 'cart' })),
      ),
    { dispatch: false },
  );

  // оформление заказа
  placeOrder$: Observable<CheckoutActions.PlaceOrderSuccess | CheckoutActions.PlaceOrderFail | CartActions.RemoveCart> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CheckoutActions.PLACE_ORDER),
        concatLatestFrom(() => [
          this.activeCartService.getActiveCartId(),
          this.currentProfileService
            .get()
            .pipe(map((profile) => (profile.isLegal ? profile.selectedCompanyUid : undefined))),
          this.checkoutService.getCustomerConsignment(),
        ]),
        switchMap(([, cartId, customerOrganizationUid, customerConsignment]) => {
          return this.checkoutApiService
            .placeOrder({
              cartId,
              customerOrganizationUid,
              customerConsignment,
              checkoutContactFormData: this.checkoutContactFormService.getValue(),
              promoCampaignParams: this.promoCampaignService.promoCampaignParams,
            })
            .pipe(
              map((res) => new CheckoutActions.PlaceOrderSuccess(res, { cartId })),
              catchError((error) => of(new CheckoutActions.PlaceOrderFail(normalizeHttpError(error)))),
            );
        }),
      ),
    );

  // сохранение данных для страницы спасибо после успешного оформления заказа
  setOrderDetailsOnSuccess$: Observable<CheckoutActions.SetOrderDetails> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.PLACE_ORDER_SUCCESS),
      map((action: CheckoutActions.PlaceOrderSuccess) => action.payload),
      switchMap((order) => of(new CheckoutActions.SetOrderDetails(order))),
    ),
  );

  // создание новой корзины после успешного оформления заказа
  // после создания, старая будет удалена автоматически, так как в createCart передается expiredCartId
  createNewCartOnSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CheckoutActions.PlaceOrderSuccess>(CheckoutActions.PLACE_ORDER_SUCCESS),
        concatLatestFrom(() => this.userIdService.getUserId()),
        tap(([action, userId]) =>
          this.multiCartService.createCart({
            userId,
            extraData: { active: true, expiredCartId: action.extraData?.cartId },
          }),
        ),
      ),
    { dispatch: false },
  );

  // переход на страницу спасибо после успешного оформления заказа
  redirectOnSuccess$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CheckoutActions.PlaceOrderSuccess>(CheckoutActions.PLACE_ORDER_SUCCESS),
        map(({ payload }) => payload),
        switchMap((order) =>
          // ждем пока создастся новая корзина
          this.actions$.pipe(
            ofType(CartActions.CREATE_CART_SUCCESS, CartActions.CREATE_CART_FAIL),
            take(1),
            map(() => order),
          ),
        ),
        filter((order) => !!order.code),
        map((order) => {
          if (order.paymentRedirectURL) {
            this.location.replaceState(['checkout', 'order-confirmation'].join('/'));
            (this.windowRef.nativeWindow as typeof window).location.href = order.paymentRedirectURL;

            return;
          }

          this.location.replaceState('/cart');
          this.routingService.go(['checkout', 'order-confirmation'].join('/'));
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private checkoutService: CheckoutService,
    private checkoutContactFormService: CheckoutContactFormService,
    private userIdService: UserIdService,
    private multiCartService: MultiCartService,
    private routingService: RoutingService,
    private activeCartService: ActiveCartService,
    private windowRef: WindowRef,
    private location: Location,
    private checkoutApiService: CheckoutApiService,
    private promoCampaignService: PromoCampaignService,
    private currentProfileService: CurrentProfileService,
  ) {}

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
    return this.actions$.pipe(
      ofType(CheckoutActions.CHECKOUT_INIT),
      exhaustMap(() => resolvedEffects$.pipe(takeUntil(this.actions$.pipe(ofType(CheckoutActions.CHECKOUT_DESTROY))))),
    );
  }
}
